import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Sidebar.css';

import {
  FaSignOutAlt,
  FaTachometerAlt,
  FaHandshake,
  FaTruckLoading,
  FaWarehouse,
  FaDollyFlatbed,
  FaCubes,
  FaClipboardList,
  FaChartBar,
  FaLightbulb,
  FaMoneyBillWave,
  FaBoxes,
  FaFileInvoiceDollar,
  FaFileContract,
  FaDatabase,
  FaTasks,
  FaPlay,
  FaChartLine,
  FaUpload,
  FaUserShield,
} from 'react-icons/fa';

const API =
  window.location.hostname === 'localhost'
    ? 'http://localhost:5000'
    : 'https://myssa1.osc-fr1.scalingo.io';

const ALL_MENU_ITEMS = [
  {
    text: 'Global dashboard',
    icon: <FaTachometerAlt />,
    link: '/home',
    roles: ['Admin', 'Manager', 'Opérateur'],
  },
  {
    text: 'Relation client',
    icon: <FaHandshake />,
    link: '/Relationclient',
    roles: ['Admin', 'Manager', 'Opérateur'],
  },
  {
    text: 'Approvisionnement',
    icon: <FaTruckLoading />,
    link: '/Approvisionnement',
    roles: ['Admin', 'Manager'],
  },
  {
    text: 'Réception',
    icon: <FaWarehouse />,
    link: '/Reception',
    roles: ['Admin', 'Manager'],
  },
  {
    text: 'Préparation - expédition',
    icon: <FaDollyFlatbed />,
    link: '/Preparationexpedition',
    roles: ['Admin', 'Manager'],
  },
  {
    text: 'Gestion des stocks',
    icon: <FaCubes />,
    link: '/Gestiondesstocks',
    roles: ['Admin', 'Manager'],
  },
  {
    text: 'Fiche Article',
    icon: <FaClipboardList />,
    link: '/Fichearticle',
    roles: ['Admin', 'Manager'],
  },
  {
    text: 'Suivi des prévisions',
    icon: <FaChartBar />,
    link: '/Suiviprevision',
    roles: ['Admin', 'Manager'],
  },
  {
    text: 'Analyse faisabilité',
    icon: <FaLightbulb />,
    link: '/Analysefaisabilite',
    roles: ['Admin', 'Manager'],
  },
  {
    text: 'Cash management',
    icon: <FaMoneyBillWave />,
    link: '/Cashmanagment',
    roles: ['Admin', 'Manager'],
  },
  {
    text: 'Stock',
    icon: <FaBoxes />,
    link: '/stock',
    roles: ['Admin', 'Manager'],
  },
  {
    text: 'Commande client',
    icon: <FaFileInvoiceDollar />,
    link: '/Commandeclient',
    roles: ['Admin', 'Manager'],
  },
  {
    text: 'Commande fournisseur',
    icon: <FaFileContract />,
    link: '/Fournisseur',
    roles: ['Admin', 'Manager'],
  },
  {
    text: 'Base Article',
    icon: <FaDatabase />,
    link: '/Donneitem',
    roles: ['Admin', 'Manager'],
  },
  {
    text: 'Tâches',
    icon: <FaTasks />,
    link: '/Taches',
    roles: ['Admin', 'Manager'],
  },
  {
    text: 'Animation',
    icon: <FaPlay />,
    link: '/Animation',
    roles: ['Admin', 'Manager'],
  },
  {
    text: 'Stock projeté',
    icon: <FaChartLine />,
    link: '/Stockprojete',
    roles: ['Admin', 'Manager'],
  },
  {
    text: 'Drag & drop',
    icon: <FaUpload />,
    link: '/Dragdrop',
    roles: ['Admin'],
  },
  {
    text: 'Gestion des droits',
    icon: <FaUserShield />,
    link: '/Gestiondesdroits',
    roles: ['Admin'],
  },
];

const formatLastUpdate = (isoString) => {
  if (!isoString) return null;

  const d = new Date(isoString);

  // Vérifie que la date est valide
  if (isNaN(d.getTime())) return null;

  const date = d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  const heure = d.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return `${date} à ${heure}`;
};

const Sidebar = ({ onLogout, user }) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(true);

  // Valeur null au départ
  const [lastUpdate, setLastUpdate] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchLastUpdate = async () => {
      try {
        const response = await fetch(`${API}/api/last-update`);

        if (!response.ok) {
          throw new Error('Erreur API');
        }

        const data = await response.json();

        // Met à jour uniquement si une vraie date existe
        if (data?.lastUpdate) {
          setLastUpdate(data.lastUpdate);
        }
      } catch (error) {
        console.error(
          'Erreur récupération dernière mise à jour :',
          error
        );
      }
    };

    // Premier chargement
    fetchLastUpdate();

    // Rafraîchit toutes les 5 minutes
   const interval = setInterval(fetchLastUpdate, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleLinkClick = (index, link) => {
    setActiveIndex(index);
    setIsCollapsed(true);
    navigate(link);
  };

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  const role = user?.role || '';

  const menuItems = ALL_MENU_ITEMS.filter((item) =>
    item.roles.includes(role)
  );

  return (
    <div
      className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}
      onMouseEnter={() => setIsCollapsed(false)}
      onMouseLeave={() => setIsCollapsed(true)}
    >
      {/* Bouton logout */}
      <div className="user-info">
        <button onClick={handleLogout} className="logout-btn">
          <FaSignOutAlt size={20} />
        </button>
      </div>

      {/* Nom + rôle */}
      {!isCollapsed && user && (
        <div
          style={{
            padding: '6px 16px 12px',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            marginBottom: '8px',
          }}
        >
          <p
            style={{
              margin: 0,
              color: 'white',
              fontWeight: 700,
              fontSize: '14px',
            }}
          >
            {user.username}
          </p>

          <p
            style={{
              margin: 0,
              color: 'rgba(255,255,255,0.6)',
              fontSize: '12px',
            }}
          >
            {user.role}
          </p>
        </div>
      )}

      {/* Menu */}
      <div className="sidebar-content">
        <ul>
          {menuItems.map((item, index) => (
            <li key={index}>
              <Link
                to={item.link}
                onClick={() => handleLinkClick(index, item.link)}
                className={activeIndex === index ? 'active' : ''}
              >
                <span className="icon">{item.icon}</span>

                <span className="text">{item.text}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* Dernière mise à jour */}
      {!isCollapsed && (
        <div
          style={{
            padding: '12px 16px',
            borderTop: '1px solid rgba(255,255,255,0.1)',
            marginTop: 'auto',
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: '10px',
              color: 'rgba(255,255,255,0.4)',
              lineHeight: 1.5,
              whiteSpace: 'pre-line',
            }}
          >
            {lastUpdate
              ? `Dernière mise à jour\nle ${formatLastUpdate(
                  lastUpdate
                )}`
              : 'Chargement...'}
          </p>
        </div>
      )}
    </div>
  );
};

export default Sidebar;